import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const EXAMPLE_URL =
  "https://user:p%40ss@example.co.uk:8443/path/to/page?q=hello%20world&tags=a&tags=b#section-2";

// Default ports for well-known schemes — used only to flag that a port
// is "default for scheme" so users don't get confused when it's absent.
const DEFAULT_PORTS = {
  "http:": "80",
  "https:": "443",
  "ws:": "80",
  "wss:": "443",
  "ftp:": "21",
};

const safeDecode = (raw) => {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const parseInput = (raw) => {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return { empty: true };
  let url;
  try {
    url = new URL(trimmed);
  } catch {
    return { valid: false, raw: trimmed };
  }
  const segments = url.pathname.split("/").filter(Boolean);
  const params = Array.from(url.searchParams.entries()).map(
    ([key, value]) => ({ key, value })
  );
  const hasAscii = /^[\x00-\x7F]*$/.test(url.hostname);
  return {
    valid: true,
    raw: trimmed,
    href: url.href,
    protocol: url.protocol,
    username: url.username,
    password: url.password,
    host: url.host,
    hostname: url.hostname,
    port: url.port,
    portIsDefault: !url.port && !!DEFAULT_PORTS[url.protocol],
    defaultPort: DEFAULT_PORTS[url.protocol] || "",
    pathname: url.pathname,
    segments,
    search: url.search,
    searchRaw: url.search.startsWith("?") ? url.search.slice(1) : url.search,
    params,
    hash: url.hash,
    hashRaw: url.hash.startsWith("#") ? url.hash.slice(1) : url.hash,
    origin: url.origin,
    isPunycode: !hasAscii || url.hostname.includes("xn--"),
  };
};

const UrlParser = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(EXAMPLE_URL);
  const [showPassword, setShowPassword] = useState(false);

  const parsed = useMemo(() => parseInput(input), [input]);

  const copy = async (text, label) => {
    if (!text) {
      toast.error(t("url_parser_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label ? `${label} ${t("copy_success")}` : t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const loadExample = () => setInput(EXAMPLE_URL);
  const clear = () => setInput("");

  const renderRow = (labelKey, value, { mono = true, copyLabel, extra } = {}) => {
    const display =
      value === "" || value == null ? (
        <span className="text-gray-400 italic">{t("url_parser_field_none")}</span>
      ) : mono ? (
        <span className="font-mono break-all">{value}</span>
      ) : (
        <span className="break-all">{value}</span>
      );
    return (
      <div className="grid grid-cols-12 gap-2 items-start border-b border-gray-100 py-2">
        <div className="col-span-12 sm:col-span-4 text-xs uppercase tracking-wide text-indigo-700 font-medium pt-1">
          {t(labelKey)}
        </div>
        <div className="col-span-12 sm:col-span-7 text-sm text-gray-800">
          {display}
          {extra}
        </div>
        <div className="col-span-12 sm:col-span-1 flex sm:justify-end">
          {value ? (
            <StandardButton
              onClick={() => copy(String(value), copyLabel ? t(copyLabel) : t(labelKey))}
              type="secondary"
              variant="outlined"
              size="small"
              aria-label={`${t("copy_button")} — ${t(labelKey)}`}
            >
              {t("copy_button")}
            </StandardButton>
          ) : null}
        </div>
      </div>
    );
  };

  const invalid = parsed && parsed.valid === false;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("url_parser_title")}
            </h1>
            <p className="text-gray-600">{t("url_parser_intro")}</p>

            <div className="flex flex-wrap gap-2">
              <StandardButton
                onClick={loadExample}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("url_parser_load_example")}
              </StandardButton>
              <StandardButton
                onClick={clear}
                type="secondary"
                variant="outlined"
                size="small"
                disabled={!input}
              >
                {t("url_parser_clear")}
              </StandardButton>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="url-parser-input"
                className="text-sm font-medium text-gray-700"
              >
                {t("url_parser_input_label")}
              </label>
              <input
                id="url-parser-input"
                type="text"
                spellCheck={false}
                autoComplete="off"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("url_parser_input_placeholder")}
                className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                aria-invalid={invalid ? "true" : "false"}
              />
            </div>

            {parsed.empty && (
              <p className="text-sm text-gray-500 italic">
                {t("url_parser_empty_hint")}
              </p>
            )}

            {invalid && (
              <div
                role="alert"
                className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm"
              >
                {t("url_parser_invalid")}
              </div>
            )}

            {parsed.valid && (
              <>
                <div className="bg-white border border-gray-200 rounded-lg p-2">
                  {renderRow("url_parser_field_href", parsed.href)}
                  {renderRow("url_parser_field_origin", parsed.origin)}
                  {renderRow("url_parser_field_protocol", parsed.protocol)}
                  {renderRow("url_parser_field_username", parsed.username)}
                  {renderRow(
                    "url_parser_field_password",
                    parsed.password
                      ? showPassword
                        ? parsed.password
                        : "•".repeat(Math.max(parsed.password.length, 4))
                      : "",
                    {
                      extra: parsed.password ? (
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="ml-2 text-xs text-indigo-700 underline"
                          aria-pressed={showPassword}
                        >
                          {showPassword
                            ? t("url_parser_password_hide")
                            : t("url_parser_password_show")}
                        </button>
                      ) : null,
                      copyLabel: "url_parser_field_password",
                    }
                  )}
                  {renderRow("url_parser_field_host", parsed.host)}
                  {renderRow("url_parser_field_hostname", parsed.hostname, {
                    extra: parsed.isPunycode ? (
                      <span className="ml-2 inline-block text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded px-2 py-0.5">
                        {t("url_parser_hostname_idn")}
                      </span>
                    ) : null,
                  })}
                  {renderRow(
                    "url_parser_field_port",
                    parsed.port,
                    {
                      extra:
                        !parsed.port && parsed.portIsDefault ? (
                          <span className="ml-2 text-xs text-gray-500 italic">
                            {t("url_parser_port_default", {
                              port: parsed.defaultPort,
                            })}
                          </span>
                        ) : null,
                    }
                  )}
                  {renderRow("url_parser_field_pathname", parsed.pathname)}
                  {renderRow("url_parser_field_search", parsed.searchRaw)}
                  {renderRow("url_parser_field_hash", parsed.hashRaw)}
                </div>

                {parsed.segments.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="text-xs uppercase tracking-wide text-indigo-700 font-medium">
                      {t("url_parser_segments_label")}
                    </div>
                    <ol className="list-decimal pl-5 text-sm font-mono space-y-1">
                      {parsed.segments.map((seg, idx) => (
                        <li key={`${idx}-${seg}`}>
                          <span className="text-gray-800">{seg}</span>
                          {safeDecode(seg) !== seg && (
                            <span className="text-gray-500 ml-2">
                              → {safeDecode(seg)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <div className="text-xs uppercase tracking-wide text-indigo-700 font-medium">
                    {t("url_parser_params_label")}
                  </div>
                  {parsed.params.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">
                      {t("url_parser_params_empty")}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 rounded-md">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-600"
                            >
                              {t("url_parser_params_key")}
                            </th>
                            <th
                              scope="col"
                              className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-600"
                            >
                              {t("url_parser_params_value")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsed.params.map((p, idx) => (
                            <tr
                              key={`${idx}-${p.key}`}
                              className="border-t border-gray-100 align-top"
                            >
                              <td className="px-3 py-2 font-mono break-all">
                                {p.key}
                              </td>
                              <td className="px-3 py-2 font-mono break-all">
                                {p.value || (
                                  <span className="text-gray-400 italic">
                                    {t("url_parser_field_none")}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="border-t border-gray-200 pt-4 mt-2 text-xs text-gray-500 leading-relaxed">
              <p>{t("url_parser_note_spec")}</p>
              <p>{t("url_parser_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default UrlParser;
